import React, { useEffect, useState } from 'react';
import { ConfigProvider } from 'antd';
import type { ConfigProviderProps } from 'antd/es/config-provider';
import type { Locale as AntdLocale } from 'antd/es/locale';
import dayjs from 'dayjs';
import { useLocale } from './index';

/**
 * antd ships its own component texts per language. This map is the only
 * per-language code in the client: add a line when a language arrives;
 * an unknown language degrades to English.
 */
const ANTD_LOCALES: Record<string, () => Promise<{ default: AntdLocale }>> = {
  en: () => import('antd/locale/en_US'),
  ru: () => import('antd/locale/ru_RU'),
  es: () => import('antd/locale/es_ES'),
};

const candidatesOf = (locale: string): string[] => {
  const lower = locale.toLowerCase();
  return Array.from(new Set([lower, lower.split('-')[0]]));
};

const loadAntdLocale = async (locale: string): Promise<AntdLocale> => {
  const code = candidatesOf(locale).find((c) => ANTD_LOCALES[c]) ?? 'en';
  try {
    return (await ANTD_LOCALES[code]()).default;
  } catch {
    return (await ANTD_LOCALES.en()).default;
  }
};

/** dayjs locales are loaded by name, so no per-language code is needed. */
const applyDayjsLocale = async (locale: string): Promise<void> => {
  for (const code of candidatesOf(locale)) {
    if (code === 'en') break;
    try {
      await import(/* webpackChunkName: "dayjs-locale-[request]" */ `dayjs/locale/${code}.js`);
      dayjs.locale(code);
      return;
    } catch {
      // Try the base language, then English.
    }
  }
  dayjs.locale('en');
};

type LocaleProviderProps = Omit<ConfigProviderProps, 'locale'> & { children: React.ReactNode };

/** antd `ConfigProvider` + dayjs bound to the current UI language. */
export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children, ...config }) => {
  const locale = useLocale();
  const [antdLocale, setAntdLocale] = useState<AntdLocale | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    void applyDayjsLocale(locale);
    loadAntdLocale(locale).then((value) => {
      if (alive) setAntdLocale(value);
    });
    return () => {
      alive = false;
    };
  }, [locale]);

  return (
    <ConfigProvider {...config} locale={antdLocale}>
      {children}
    </ConfigProvider>
  );
};

export default LocaleProvider;
